// Runtime API client for the 100X Equine sales workspace.
//
// Wraps fetch to the runtime API server (services/api-server) with:
//   - Bearer token supplied from the HubSpot SSO context
//   - Consistent error mapping for 401, 403, 404, 429, 500, 502, 504
//   - Exponential backoff retry on 429 (default 3 attempts, honoring
//     Retry-After header or retryAfterMs body field)
//   - Injectable fetch impl for tests
//
// No live network calls in tests. Callers pass their own fetchImpl.

import type {
  ActionRequestBody,
  ActionResponse,
  AuditQueryRequest,
  AuditQueryResponse,
  GateResponse,
  HealthResponse,
  RuntimeApiErrorKind,
  RuntimeApiErrorShape,
} from './types.js';
import { defaultFetch } from './default-fetch.js';

export class RuntimeApiError extends Error implements RuntimeApiErrorShape {
  readonly kind: RuntimeApiErrorKind;
  readonly status: number;
  readonly errorCode?: string;
  readonly retryAfterMs?: number;
  readonly traceId?: string;
  readonly body?: unknown;

  constructor(init: RuntimeApiErrorShape) {
    super(init.message);
    this.name = 'RuntimeApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.errorCode = init.errorCode;
    this.retryAfterMs = init.retryAfterMs;
    this.traceId = init.traceId;
    this.body = init.body;
  }
}

export type FetchImpl = typeof fetch;

export type TokenProvider = () => string | null | Promise<string | null>;

export type SleepImpl = (ms: number) => Promise<void>;

export interface RuntimeApiClientOptions {
  baseUrl: string;
  getToken: TokenProvider;
  fetchImpl?: FetchImpl;
  sleep?: SleepImpl;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  retryMaxDelayMs?: number;
  defaultTimeoutMs?: number;
}

// Discriminant to reduce the retry choice to a single value.
type RetryDecision =
  | { retry: false }
  | { retry: true; delayMs: number };

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_BASE_DELAY_MS = 400;
const DEFAULT_RETRY_MAX_DELAY_MS = 8_000;
const DEFAULT_TIMEOUT_MS = 30_000;

// Status codes mapped to a stable client-side kind.
function kindForStatus(status: number): RuntimeApiErrorKind {
  if (status === 401) return 'AUTH_FAILED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 400) return 'VALIDATION_ERROR';
  if (status === 502) return 'VENDOR_ERROR';
  if (status === 504) return 'TIMEOUT';
  if (status >= 500) return 'SERVER_ERROR';
  return 'SERVER_ERROR';
}

function parseRetryAfterHeader(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    // HTTP Retry-After in seconds.
    return Math.round(asNumber * 1000);
  }
  const asDate = Date.parse(raw);
  if (!Number.isNaN(asDate)) {
    const delta = asDate - Date.now();
    return delta > 0 ? delta : 0;
  }
  return undefined;
}

function pickRetryAfter(
  headers: Headers,
  body: unknown,
): number | undefined {
  const fromHeader = parseRetryAfterHeader(headers.get('Retry-After'));
  if (fromHeader !== undefined) return fromHeader;
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    typeof (body as { retryAfterMs?: unknown }).retryAfterMs === 'number'
  ) {
    return (body as { retryAfterMs: number }).retryAfterMs;
  }
  return undefined;
}

function computeBackoff(
  attempt: number,
  baseMs: number,
  maxMs: number,
  serverHintMs: number | undefined,
): number {
  if (serverHintMs !== undefined) {
    return Math.min(Math.max(serverHintMs, 0), maxMs);
  }
  // Full jitter: random between 0 and min(cap, base * 2^attempt).
  const ceiling = Math.min(maxMs, baseMs * 2 ** attempt);
  const jittered = Math.floor(Math.random() * (ceiling + 1));
  return jittered;
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  try {
    const text = await response.text();
    return text || null;
  } catch {
    return null;
  }
}

function normalizeBaseUrl(baseUrl: string): string {
  if (!baseUrl || typeof baseUrl !== 'string') {
    throw new Error('BASE_URL_REQUIRED');
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

// R2: broadened method type to carry draft/history PATCH + DELETE calls.
// GET + POST retry semantics are unchanged; the new methods inherit the
// same retry rules at the base client but mutation callers (WorkClient
// and DraftsApi) construct a 0-retry variant to enforce at-most-once.
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method: HttpMethod;
  path: string;
  body?: unknown;
  traceId?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  // Extra request headers to merge in alongside Accept, Authorization and
  // Content-Type. Subclasses (SignedRuntimeApiClient) use this to attach
  // X-Customer-Id / X-Work-Id / X-Portal-Id from the current session.
  // Reserved header names (Authorization, Accept, Content-Type, X-Trace-Id)
  // are not overridable here; they are set by the base client.
  headers?: Record<string, string>;
}

export class RuntimeApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly fetchImpl: FetchImpl;
  private readonly sleep: SleepImpl;
  private readonly maxRetries: number;
  private readonly retryBaseDelayMs: number;
  private readonly retryMaxDelayMs: number;
  private readonly defaultTimeoutMs: number;

  constructor(options: RuntimeApiClientOptions) {
    this.baseUrl = normalizeBaseUrl(options.baseUrl);
    this.getToken = options.getToken;
    // W38: never the bare `fetch` (see default-fetch.ts: Illegal invocation).
    this.fetchImpl = options.fetchImpl ?? defaultFetch;
    this.sleep = options.sleep ?? defaultSleep;
    this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.retryBaseDelayMs =
      options.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;
    this.retryMaxDelayMs =
      options.retryMaxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS;
    this.defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  async fireAction(
    actionName: string,
    body: ActionRequestBody,
    opts: { traceId?: string; signal?: AbortSignal } = {},
  ): Promise<ActionResponse> {
    if (!/^[A-Z][A-Z0-9_]{2,63}$/.test(actionName)) {
      throw new RuntimeApiError({
        kind: 'VALIDATION_ERROR',
        status: 0,
        message: 'Action name is not the required A-Z_ shape',
        errorCode: 'ACTION_NAME_INVALID',
      });
    }
    return this.request<ActionResponse>({
      method: 'POST',
      path: `/v1/actions/${actionName}`,
      body,
      ...(opts.traceId !== undefined ? { traceId: opts.traceId } : {}),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  }

  async readGate(
    gateName: string,
    opts: { traceId?: string; signal?: AbortSignal } = {},
  ): Promise<GateResponse> {
    return this.request<GateResponse>({
      method: 'GET',
      path: `/v1/gates/${gateName}`,
      ...(opts.traceId !== undefined ? { traceId: opts.traceId } : {}),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  }

  async queryAudit(
    body: AuditQueryRequest,
    opts: { traceId?: string; signal?: AbortSignal } = {},
  ): Promise<AuditQueryResponse> {
    return this.request<AuditQueryResponse>({
      method: 'POST',
      path: '/v1/audit/query',
      body,
      ...(opts.traceId !== undefined ? { traceId: opts.traceId } : {}),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  }

  async checkHealth(
    opts: { signal?: AbortSignal } = {},
  ): Promise<HealthResponse> {
    return this.request<HealthResponse>({
      method: 'GET',
      path: '/v1/health',
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  }

  async request<T>(options: RequestOptions): Promise<T> {
    const { method, path, body, traceId, signal, timeoutMs, headers: extraHeaders } = options;
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    const effectiveTimeoutMs = timeoutMs ?? this.defaultTimeoutMs;
    let attempt = 0;

    // Retry loop. Only 429 responses and transient network errors trigger a
    // retry. Everything else surfaces immediately.
    // Upper bound: 1 + maxRetries attempts total.
    while (true) {
      const token = await this.getToken();
      const headers: Record<string, string> = {
        Accept: 'application/json',
      };
      // Caller-supplied headers go in first so the reserved base headers
      // below cannot be overridden by them.
      if (extraHeaders) {
        for (const [k, v] of Object.entries(extraHeaders)) {
          if (typeof v === 'string' && v.length > 0) headers[k] = v;
        }
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
      }
      if (traceId) {
        headers['X-Trace-Id'] = traceId;
      }

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), effectiveTimeoutMs);
      const abortFromCaller = () => controller.abort();
      if (signal) {
        if (signal.aborted) {
          clearTimeout(timer);
          throw new RuntimeApiError({
            kind: 'TIMEOUT',
            status: 0,
            message: 'Request aborted before dispatch',
            errorCode: 'ABORTED',
          });
        }
        signal.addEventListener('abort', abortFromCaller, { once: true });
      }

      let response: Response;
      try {
        response = await this.fetchImpl(url, {
          method,
          headers,
          body: body === undefined ? undefined : JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (err) {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', abortFromCaller);
        // Distinguish caller abort from network failure.
        if (signal?.aborted || controller.signal.aborted) {
          throw new RuntimeApiError({
            kind: 'TIMEOUT',
            status: 0,
            message: 'Request aborted (timeout or caller signal)',
            errorCode: 'ABORTED',
          });
        }
        // Network-level failure: retry with backoff if attempts remain.
        const decision = this.decideRetry(attempt, undefined);
        if (decision.retry) {
          attempt++;
          await this.sleep(decision.delayMs);
          continue;
        }
        throw new RuntimeApiError({
          kind: 'NETWORK_ERROR',
          status: 0,
          message: err instanceof Error ? err.message : 'Network failure',
          errorCode: 'NETWORK_ERROR',
        });
      } finally {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', abortFromCaller);
      }

      const parsedBody = await readBody(response);
      const traceIdOut = response.headers.get('X-Trace-Id') || undefined;

      if (response.ok) {
        return parsedBody as T;
      }

      // Rate limited: honor server-provided delay and retry.
      if (response.status === 429) {
        const retryAfterMs = pickRetryAfter(response.headers, parsedBody);
        const decision = this.decideRetry(attempt, retryAfterMs);
        if (decision.retry) {
          attempt++;
          await this.sleep(decision.delayMs);
          continue;
        }
        throw new RuntimeApiError({
          kind: 'RETRY_EXHAUSTED',
          status: 429,
          message: 'Rate limit exceeded after retry budget was spent',
          errorCode: 'RATE_LIMITED',
          ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
          ...(traceIdOut !== undefined ? { traceId: traceIdOut } : {}),
          body: parsedBody,
        });
      }

      // Any other non-2xx: surface immediately.
      const errorCode = extractErrorCode(parsedBody);
      throw new RuntimeApiError({
        kind: kindForStatus(response.status),
        status: response.status,
        message:
          extractErrorMessage(parsedBody) ||
          `Runtime API returned ${response.status}`,
        ...(errorCode !== undefined ? { errorCode } : {}),
        ...(traceIdOut !== undefined ? { traceId: traceIdOut } : {}),
        body: parsedBody,
      });
    }
  }

  private decideRetry(
    attempt: number,
    serverHintMs: number | undefined,
  ): RetryDecision {
    if (attempt >= this.maxRetries) return { retry: false };
    const delayMs = computeBackoff(
      attempt,
      this.retryBaseDelayMs,
      this.retryMaxDelayMs,
      serverHintMs,
    );
    return { retry: true, delayMs };
  }
}

function extractErrorCode(body: unknown): string | undefined {
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    typeof (body as { errorCode?: unknown }).errorCode === 'string'
  ) {
    return (body as { errorCode: string }).errorCode;
  }
  return undefined;
}

function extractErrorMessage(body: unknown): string | undefined {
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    typeof (body as { errorMessage?: unknown }).errorMessage === 'string'
  ) {
    return (body as { errorMessage: string }).errorMessage;
  }
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    typeof (body as { error?: unknown }).error === 'string'
  ) {
    return (body as { error: string }).error;
  }
  return undefined;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Convenience factory for consumers that already have base URL + token
// resolved from env and the HubSpot context.
export function createRuntimeApiClient(
  options: RuntimeApiClientOptions,
): RuntimeApiClient {
  return new RuntimeApiClient(options);
}
